import { useMemo } from 'react';
import {
  buildEntityIndex,
  resolveRef,
  type Adventure,
  type FieldDescriptor,
  type KindDef,
} from '@gm-binder/shared';
import { Card, MarkdownView, MediaImage } from '../ui';
import { mediaUrl } from '../../api/client';
import { useRefNav } from '../../lib/refNav';
import {
  entityIcon,
  entityTitle,
  formatScalar,
  roleField,
  scalarFields,
} from '../../lib/viewRegistry';
import styles from './Generic.module.css';

type Entity = Record<string, unknown>;

function subtitleOf(kind: KindDef, e: Entity): string | undefined {
  const key = roleField(kind, 'subtitle')?.key;
  const v = key ? e[key] : undefined;
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function imageOf(kind: KindDef, e: Entity): string | undefined {
  const key = roleField(kind, 'image')?.key;
  const v = key ? e[key] : undefined;
  return typeof v === 'string' && v ? v : undefined;
}

function bodySnippet(kind: KindDef, e: Entity): string | undefined {
  const key = roleField(kind, 'body')?.key;
  const v = key ? e[key] : undefined;
  if (typeof v !== 'string' || !v.trim()) return undefined;
  return v.length > 140 ? `${v.slice(0, 140)}…` : v;
}

export interface GenericListProps {
  kind: KindDef;
  entities: Entity[];
  slug: string;
  onOpen?: (entity: Entity) => void;
}

/** Generic list template that renders any kind from its field descriptors + role hints. */
export function GenericList({ kind, entities, slug, onOpen }: GenericListProps) {
  const view = kind.views.list;

  if (entities.length === 0) {
    return <p className={styles.empty}>Nessun elemento.</p>;
  }

  if (view === 'gallery') {
    return (
      <div className={styles.gallery}>
        {entities.map((e) => (
          <button key={String(e.id)} className={styles.galleryItem} onClick={() => onOpen?.(e)}>
            <MediaImage
              src={mediaUrl(slug, imageOf(kind, e))}
              alt={entityTitle(kind, e)}
              fallback={kind.icon}
            />
            <span>{entityTitle(kind, e)}</span>
          </button>
        ))}
      </div>
    );
  }

  if (view === 'table') {
    const cols = scalarFields(kind).slice(0, 5);
    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Nome</th>
            {cols.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entities.map((e) => (
            <tr
              key={String(e.id)}
              className={onOpen ? styles.clickable : undefined}
              onClick={() => onOpen?.(e)}
            >
              <td>
                {entityIcon(kind, e)} {entityTitle(kind, e)}
              </td>
              {cols.map((c) => (
                <td key={c.key}>{formatScalar(e[c.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (view === 'list') {
    return (
      <div className={styles.list}>
        {entities.map((e) => (
          <button key={String(e.id)} className={styles.listRow} onClick={() => onOpen?.(e)}>
            <span className={styles.icon} aria-hidden>
              {entityIcon(kind, e)}
            </span>
            <span className={styles.title}>{entityTitle(kind, e)}</span>
            {subtitleOf(kind, e) && <span className={styles.subtitle}>{subtitleOf(kind, e)}</span>}
          </button>
        ))}
      </div>
    );
  }

  // cards / grid / journal / statblockCards / mapGrouped → card grid
  return (
    <div className={styles.cards}>
      {entities.map((e) => {
        const img = imageOf(kind, e);
        return (
          <Card key={String(e.id)} padded={false} className={styles.card}>
            <button className={styles.cardBtn} onClick={() => onOpen?.(e)}>
              {img && (
                <MediaImage
                  src={mediaUrl(slug, img)}
                  alt={entityTitle(kind, e)}
                  fallback={kind.icon}
                  className={styles.cardImg}
                />
              )}
              <div className={styles.cardBody}>
                <div className={styles.cardTop}>
                  <span className={styles.icon} aria-hidden>
                    {entityIcon(kind, e)}
                  </span>
                  <span className={styles.title}>{entityTitle(kind, e)}</span>
                </div>
                {subtitleOf(kind, e) && <div className={styles.subtitle}>{subtitleOf(kind, e)}</div>}
                {bodySnippet(kind, e) && <p className={styles.snippet}>{bodySnippet(kind, e)}</p>}
              </div>
            </button>
          </Card>
        );
      })}
    </div>
  );
}

function RefValue({
  adventure,
  raw,
  target,
}: {
  adventure: Adventure;
  raw: unknown;
  target?: string | string[];
}) {
  const refNav = useRefNav();
  const index = useMemo(() => buildEntityIndex(adventure), [adventure]);
  const ref =
    raw && typeof raw === 'object' && 'ref' in raw
      ? String((raw as { ref: string }).ref)
      : String(raw ?? '');
  const fromKind = Array.isArray(target) ? target[0] : target;
  const resolved = resolveRef(index, ref, fromKind);
  const label = resolved?.label ?? ref;
  if (!ref) return <span className={styles.muted}>—</span>;
  if (resolved && refNav) {
    return (
      <button
        type="button"
        className={styles.refLink}
        onClick={() => refNav.navigateRef(resolved.ref)}
      >
        {resolved.icon ?? '🔗'} {label}
      </button>
    );
  }
  return <span>{label}</span>;
}

export interface GenericDetailProps {
  kind: KindDef;
  entity: Entity;
  slug: string;
  adventure: Adventure;
}

/** Generic detail (profile/page) template rendering any kind from its field descriptors. */
export function GenericDetail({ kind, entity, slug, adventure }: GenericDetailProps) {
  const view = kind.views.detail;
  const bodyKey = roleField(kind, 'body')?.key;
  const img = imageOf(kind, entity);
  const refFields = kind.fields.filter((f) => f.type === 'ref' || f.type === 'refList');
  const refCountFields = kind.fields.filter((f) => f.type === 'refCount');
  const scalars = scalarFields(kind).filter((f) => f.type !== 'ref' && f.type !== 'refList');
  const markdownFields = kind.fields.filter((f) => f.type === 'markdown' && f.key !== bodyKey);
  const tagFields = kind.fields.filter((f) => f.type === 'tags');

  if (view === 'page') {
    return <MarkdownView>{bodyKey ? String(entity[bodyKey] ?? '') : ''}</MarkdownView>;
  }

  return (
    <div className={styles.detail}>
      {img && (
        <MediaImage
          src={mediaUrl(slug, img)}
          alt={entityTitle(kind, entity)}
          fallback={kind.icon}
          className={styles.detailImg}
        />
      )}

      {subtitleOf(kind, entity) && (
        <div className={styles.detailSubtitle}>{subtitleOf(kind, entity)}</div>
      )}

      {scalars.length > 0 && (
        <dl className={styles.fieldGrid}>
          {scalars.map((f: FieldDescriptor) => {
            const v = entity[f.key];
            if (v == null || v === '') return null;
            return (
              <div key={f.key} className={styles.fieldRow}>
                <dt>{f.label}</dt>
                <dd>
                  {typeof v === 'string' ? <MarkdownView inline>{v}</MarkdownView> : formatScalar(v)}
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      {refFields.map((f) => {
        const v = entity[f.key];
        if (v == null || (Array.isArray(v) && v.length === 0)) return null;
        const items = Array.isArray(v) ? v : [v];
        return (
          <div key={f.key} className={styles.refBlock}>
            <div className={styles.blockLabel}>{f.label}</div>
            <div className={styles.refRow}>
              {items.map((it, i) => (
                <RefValue key={i} adventure={adventure} raw={it} target={f.target} />
              ))}
            </div>
          </div>
        );
      })}

      {refCountFields.map((f) => {
        const v = entity[f.key];
        const items = Array.isArray(v) ? (v as unknown[]) : [];
        if (!items.length) return null;
        return (
          <div key={f.key} className={styles.refBlock}>
            <div className={styles.blockLabel}>{f.label}</div>
            <div className={styles.refRow}>
              {items.map((it, i) => {
                const o = (it ?? {}) as { id?: unknown; count?: unknown };
                return (
                  <span key={i} className={styles.countChip}>
                    <strong>{Number(o.count) || 1}×</strong>
                    <RefValue adventure={adventure} raw={String(o.id ?? '')} target={f.target} />
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}

      {bodyKey && typeof entity[bodyKey] === 'string' && entity[bodyKey] && (
        <Card>
          <MarkdownView>{String(entity[bodyKey])}</MarkdownView>
        </Card>
      )}

      {markdownFields.map((f) =>
        typeof entity[f.key] === 'string' && entity[f.key] ? (
          <Card key={f.key}>
            <div className={styles.blockLabel}>{f.label}</div>
            <MarkdownView>{String(entity[f.key])}</MarkdownView>
          </Card>
        ) : null,
      )}

      {tagFields.map((f) =>
        Array.isArray(entity[f.key]) && (entity[f.key] as unknown[]).length ? (
          <div key={f.key} className={styles.refBlock}>
            <div className={styles.blockLabel}>{f.label}</div>
            <div className={styles.detailList}>
              {(entity[f.key] as unknown[]).map((t, i) => (
                <div key={i} className={styles.detailRow}>
                  <MarkdownView inline>{String(t)}</MarkdownView>
                </div>
              ))}
            </div>
          </div>
        ) : null,
      )}
    </div>
  );
}
